<template>
  <main class="flex h-screen flex-col">
    <MainPageHeader
      ref="mainPageHeaderRef"
      :search-app-type="FlowType.AVIA"
      :show-burger-menu="!isNewBottomNavVariant"
      @onStartLoading="onStartLoading"
      @onMyTicketsClick="handleMyTicketsClick"
      @onMiniAppSwitch="handleMiniAppSwitch"
    >
      <template #default>
        <section
          class="bg-background-neutral-primary relative space-y-6 rounded-t-3xl px-4 pt-4"
          :class="[isNewBottomNavVariant ? 'pb-24' : 'pb-4']"
        >
          <div data-scroll-block="search_form">
            <SearchFormRedesigned
              v-if="isMainPageRedesignVariant"
              source="main_page"
              :variant="mainPageVariant"
              @onSearchFormSubmit="onSearchFormSubmit"
            />
            <SearchForm
              v-else
              source="main_page"
              :variant="mainPageVariant"
              @onSearchFormSubmit="onSearchFormSubmit"
            />
          </div>
          <div
            v-if="!isMainPageRedesignLoading"
            data-scroll-block="recent_searches"
          >
            <RecentSearchesRedesigned
              v-if="isMainPageRedesignVariant"
              :variant="mainPageVariant"
              @onSearchClick="onRecentSearchClick"
            />
            <RecentSearches
              v-else
              :variant="mainPageVariant"
              @onSearchClick="onRecentSearchClick"
            />
          </div>
          <BloomreachBanner :placement="BannerPlacement.MAIN" />
          <div v-if="!isNewBottomNavVariant" data-scroll-block="info_links">
            <InfoLinksList />
          </div>
          <div data-scroll-block="recommended_offers">
            <RecommendedOffers />
          </div>
          <div data-scroll-block="special_offers">
            <SpecialOfferCarousel />
          </div>
          <div data-scroll-block="hot_offers">
            <HotOffers class="mt-6">
              <div data-scroll-block="blog">
                <BlogArticle
                  v-if="!isError"
                  class="w-full"
                  :details="details"
                  :is-loading="isLoading"
                  @onNavigateToArticle="handleBlogClick"
                />
              </div>
            </HotOffers>
          </div>
        </section>
      </template>
      <template v-if="isNewBottomNavVariant" #rightExtra>
        <CashbackHeaderButton
          :cashback-percent="cashbackDetails.totalCashback.value"
          :is-loading="cashbackDetails.isLoading.value"
          :is-error="cashbackDetails.isError.value"
          @onClick="bottomSheet.show(CASHBACK_BOTTOM_SHEET_NAME)"
        />
      </template>
    </MainPageHeader>
    <Teleport v-if="isNewBottomNavVariant" to="body">
      <CashbackInfoBottomSheet :name="CASHBACK_BOTTOM_SHEET_NAME" />
    </Teleport>
    <div
      v-if="isAiChatVisible"
      class="fixed inset-x-4 z-40 flex items-end justify-end rounded-3xl duration-200"
      :class="isNewBottomNavVariant ? 'bottom-24' : 'bottom-4'"
    >
      <AiChatButton :is-expanded="true" @click="handleShowBottomSheet" />
    </div>
    <BottomNav v-if="isNewBottomNavVariant" />
    <!--<TravelerBanner :bottom-offset="isNewBottomNavVariant ? 96 : 16" />-->
    <div v-if="isMiniAppLoading" class="fixed left-0 top-0 z-50">
      <FullScreenLoader />
    </div>
    <AiChatBottomSheet v-if="isAiChatVisible" />
    <PromocodeModal
      v-if="promocodeModal.shouldOpen.value && promocodeModal.promocode.value"
      :promocode="promocodeModal.promocode.value"
      :banner-image="promocodeModal.bannerImage.value"
    />
  </main>
</template>

<script setup lang="ts">
  import {
    computed,
    onMounted,
    ref,
    useTemplateRef,
    watch,
    nextTick,
  } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { controllerModel } from 'shared';
  import {
    FlowType,
    FullScreenLoader,
    appNavLinks,
    App,
    MainPageHeader,
    CashbackHeaderButton,
    amplitudeExperiment,
    amplitudeLib,
  } from 'freedom-shared';

  import { BottomNav } from '@/widgets/bottom-nav';
  import { BlogArticle, useFetchBlogArticle } from '@/widgets/blog-article';
  import { InfoLinksList } from '@/widgets/info-links';
  import {
    AiChatButton,
    AiChatBottomSheet,
    useAiChatBottomSheet,
    aiChatOnboardingState,
    aiChatApi,
    AI_CHAT_SESSION_STORAGE_KEY,
  } from '@/widgets/ai-chat';
  import {
    RecentSearches,
    RecentSearchesRedesigned,
  } from '@/widgets/recent-searches';
  import { SpecialOfferCarousel } from '@/widgets/special-offer';
  // import { TravelerBanner } from '@/widgets/traveler-banner';
  import { SearchForm, SearchFormRedesigned } from '@/widgets/search-form';
  import { useAnimatedFlowExperiment } from '@/widgets/search-form/model/use-animated-flow-experiment';

  import { HotOffers } from '@/features/hot-offers';
  import { RecommendedOffers } from '@/features/recommended-offers';
  import {
    PROMOCODE_MODAL_NAME,
    PromocodeModal,
    usePromocodeModal,
  } from '@/features/promocode-modal';
  import { useFreedomFriendsSelfInfo } from '@/features/friends';

  import { BloomreachBanner } from '@/entities/bloomreach-banner';
  import {
    CashbackInfoBottomSheet,
    useFreedomCashbackDetails,
  } from '@/entities/casbhack-info';
  import { lastSearchModel } from '@/entities/last-search';
  import type { RecentSearch } from '@/entities/recent-searches';

  import { api } from '@/shared/api';
  import { amplitudeEventsConfig } from '@/shared/config';
  import {
    type MainPageVariant,
    analyticsEventsLib,
    BannerPlacement,
    buildHashCodeFromRecentSearch,
    amplitudeEventsLib,
  } from '@/shared/lib';

  import { useMainPageScroll } from './use-main-page-scroll';

  type RedirectionSource =
    | App.CABINET
    | App.PASSENGERS
    | FlowType.RAILWAYS
    | FlowType.TOURS
    | undefined;

  const props = defineProps<{ source: RedirectionSource }>();
  useFreedomFriendsSelfInfo();

  const CASHBACK_BOTTOM_SHEET_NAME = 'header-cashback-bottom-sheet';
  const bottomSheet = controllerModel.useBottomSheet();
  const cashbackDetails = useFreedomCashbackDetails();

  const mainPageHeaderRef = useTemplateRef('mainPageHeaderRef');
  useMainPageScroll(mainPageHeaderRef.value?.scrollableRef);

  const { isVariant: isNewBottomNavVariant } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.NEW_BOTTOM_NAV,
      'bottom_nav_bar',
    );

  const {
    isVariant: isAiChatEnabled,
    isExperimentLoading: isAiChatExperimentLoading,
  } = amplitudeExperiment.lib.checkExperimentVariant(
    amplitudeExperiment.config.flags.AI_CHAT_ENABLED,
    'a',
  );

  const {
    isVariant: isMainPageRedesignVariant,
    isExperimentLoading: isMainPageRedesignLoading,
  } = amplitudeExperiment.lib.checkExperimentVariant(
    amplitudeExperiment.config.flags.MAIN_PAGE_REDESIGN,
    'new_variant',
  );

  const mainPageVariant = computed<MainPageVariant>(() =>
    isMainPageRedesignVariant.value ? 'new_variant' : 'old_variant',
  );

  const animatedFlowExperiment = useAnimatedFlowExperiment();

  const modal = controllerModel.useModal();

  const promocodeModal = usePromocodeModal();
  /**
   * Time to wait for the loader to disappear for navigation to the article
   * */
  const TIMEOUT_FOR_LOADER_DISAPPEAR = 3000;
  const router = useRouter();
  const route = useRoute();
  const { isError, isLoading, details } = useFetchBlogArticle();
  const { showBottomSheet } = useAiChatBottomSheet();
  const aiChatClient = aiChatApi(api.client);

  const isMiniAppLoading = ref(false);
  const isAiChatAvailable = ref(false);
  const isAiChatAvailabilityLoading = ref(false);
  const isAiChatAutoOpenHandled = ref(false);
  // это для того чтобы открыть чат принудительно через браузер
  // нужно для тестирования
  const isAiChatForceOpen = ref(false);
  const isAiChatVisible = computed(() => {
    return (
      (!isAiChatExperimentLoading.value &&
        isAiChatEnabled.value &&
        isAiChatAvailable.value &&
        !isAiChatAvailabilityLoading.value) ||
      isAiChatForceOpen.value
    );
  });

  const initializeAiChatAvailability = async () => {
    if (!isAiChatEnabled.value && !isAiChatForceOpen.value) {
      return;
    }

    isAiChatAvailabilityLoading.value = true;
    isAiChatAvailable.value = false;
    const storedSessionId = localStorage.getItem(AI_CHAT_SESSION_STORAGE_KEY);

    try {
      if (storedSessionId) {
        await aiChatClient.getSession(storedSessionId);
        isAiChatAvailable.value = true;
        return;
      }

      const createdSessionId = await aiChatClient.createSession();

      if (createdSessionId) {
        localStorage.setItem(AI_CHAT_SESSION_STORAGE_KEY, createdSessionId);
        isAiChatAvailable.value = true;
      }
    } catch (error) {
      isAiChatAvailable.value = false;
      localStorage.removeItem(AI_CHAT_SESSION_STORAGE_KEY);
      console.error('AI chat is unavailable:', error);
    } finally {
      isAiChatAvailabilityLoading.value = false;
    }
  };

  const handleShowBottomSheet = () => {
    if (!isAiChatVisible.value) {
      return;
    }

    amplitudeEventsLib.aiChatOpen();

    showBottomSheet();
  };

  const handleAiChatAutoOpen = () => {
    if (!isAiChatVisible.value || isAiChatAutoOpenHandled.value) {
      return;
    }

    if (route.query.back === 'chat') {
      handleShowBottomSheet();
      router.replace({
        query: {
          back: '',
        },
      });
      isAiChatAutoOpenHandled.value = true;
      return;
    }

    if (!aiChatOnboardingState.getAiChatOnboardingVisibility()) {
      aiChatOnboardingState.setAiChatOnboardingVisibility(true);
    }

    isAiChatAutoOpenHandled.value = true;
  };

  const onStartLoading = () => {
    isMiniAppLoading.value = true;
    setTimeout(() => {
      isMiniAppLoading.value = false;
    }, TIMEOUT_FOR_LOADER_DISAPPEAR);
  };

  const handleMyTicketsClick = () => {
    analyticsEventsLib.myTicketsPageOpen('menu');
  };

  const handleMiniAppSwitch = (app: FlowType) => {
    onStartLoading();

    /*
     * After caching, switching is very fast, so we need to set loader on
     * and open new app only after 150ms (approximately ok time period)
     * to prevent black background blinking
     * */
    setTimeout(() => {
      switch (app) {
        case FlowType.RAILWAYS:
          appNavLinks.openRailways(FlowType.AVIA);
          break;
        case FlowType.TOURS:
          appNavLinks.openTours(FlowType.AVIA);
          break;
        case FlowType.HOTELS:
          appNavLinks.openHotels(FlowType.AVIA);
          break;
      }
    }, 150);
  };

  const handleBlogClick = () => {
    analyticsEventsLib.blogClick();
    onStartLoading();
  };

  const onSearchFormSubmit = (hashCode: string, query?: string) => {
    let source = route.query.source as string | null;

    if (source !== 'quiz') {
      source = amplitudeEventsConfig.SERP_OPEN_SOURCE.MAIN_PAGE;
    }

    router.push({
      name: 'OffersResults',
      params: {
        hashCode,
        source,
      },
      query: { iata: query },
    });
  };

  onMounted(() => {
    const source =
      (route.query.source as string | null) || props.source || null;
    analyticsEventsLib.mainPageOpen(source);
  });

  watch(
    [isAiChatExperimentLoading, isAiChatEnabled],
    async ([isLoading, isEnabled]) => {
      if (isLoading) {
        return;
      }

      if (localStorage.getItem('free-avia:ai-chat-force-open')) {
        isAiChatForceOpen.value = true;
        await initializeAiChatAvailability();
        return;
      }

      if (!isEnabled) {
        isAiChatAvailable.value = false;
        isAiChatAvailabilityLoading.value = false;
        isAiChatAutoOpenHandled.value = true;
        return;
      }

      if (isAiChatAvailable.value || isAiChatAvailabilityLoading.value) {
        return;
      }

      await initializeAiChatAvailability();
    },
    { immediate: true },
  );

  watch(
    isAiChatVisible,
    isVisible => {
      if (!isVisible) {
        return;
      }

      setTimeout(() => {
        handleAiChatAutoOpen();
      }, 500);
    },
    { immediate: true },
  );

  const onRecentSearchClick = (search: RecentSearch) => {
    const hashCode = buildHashCodeFromRecentSearch(search);

    if (!hashCode) return;

    lastSearchModel.lastSearch.saveSearchParams({
      routes: search.routes,
      passengers: search.passengers,
      cabinClass: search.cabinClass,
      isYouth: search.isYouth,
    });

    router.push({
      name: 'OffersResults',
      params: {
        hashCode,
        source: amplitudeEventsConfig.SERP_OPEN_SOURCE.LAST_SEARCHES,
      },
    });
  };

  let mainPageOpenEventFired = false;
  watch(
    isMainPageRedesignLoading,
    loading => {
      if (loading || mainPageOpenEventFired) {
        return;
      }
      amplitudeLib.setUserProperties({
        main_page_variant: mainPageVariant.value,
      });
      const source =
        (route.query.source as string | null) || props.source || null;
      analyticsEventsLib.mainPageOpen(source, mainPageVariant.value);
      mainPageOpenEventFired = true;
    },
    { immediate: true },
  );

  let animatedFlowAssignedFired = false;
  watch(
    [
      animatedFlowExperiment.isExperimentLoading,
      animatedFlowExperiment.variant,
    ],
    ([loading, variant]) => {
      if (loading || animatedFlowAssignedFired || !variant) {
        return;
      }
      amplitudeLib.setUserProperties({
        animated_flow_variant: variant,
      });
      analyticsEventsLib.animatedFlowAssigned(variant);
      animatedFlowAssignedFired = true;
    },
    { immediate: true },
  );

  watch(
    promocodeModal.shouldOpen,
    shouldOpen => {
      if (!shouldOpen) {
        return;
      }
      nextTick(() => {
        modal.show(PROMOCODE_MODAL_NAME);
      });
    },
    { immediate: true },
  );
</script>
